/* eslint-disable jsx-a11y/anchor-is-valid */
import {
    Fragment,
    KeyboardEvent,
    TouchEvent,
    useEffect,
    useRef,
    useState
} from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { ProductKeyTypes } from "@/libraries/wpl/foundations/src";

import { Tracking } from "../../../../analytics/atl-analytics/tracking";
import { useOutsideEvent } from "../../../../utils/useOutsideEvent";

type ProductNavDropdownItem = {
    label: string;
    labelEnglish?: string;
    href: string;
    isSelected?: boolean;
};

export type ProductNavDropdownItemGroup = {
    header: string;
    items: Array<ProductNavDropdownItem>;
    hideHeader?: boolean;
};

export interface ProductNavDropdownProps {
    label: string;
    isMobile?: boolean;
    groups?: Array<ProductNavDropdownItemGroup>;
    dataProductKey?: ProductKeyTypes;
}

const parentItemStyle = css({
    "position": "relative",
    "insetBlockStart": "3px",
    "display": "flex",
    "alignItems": "center",
    "fontSize": "1rem",
    "lineHeight": 1,
    "color": "#0052cc",
    "&:hover": {
        "color": "#0065ff",
        "&::after": {
            [media.above.md]: {
                transform: "translate3d(0, 2px, 0) rotate(90deg)",
                transition: "transform 250ms"
            }
        }
    },
    "&::after": {
        [media.above.md]: {
            background: `url("https://wac-cdn.atlassian.com/assets/img/wac/arrow-right-color.svg") no-repeat scroll center center`,
            backgroundSize: "contain",
            content: '""',
            display: "inline-block",
            marginInlineStart: "6px",
            width: "12px",
            height: "12px",
            transform: "translate3d(0, 0, 0) rotate(90deg)",
            transition: "transform 250ms"
        },
        [media.below.md]: {
            content: '""',
            width: "6px",
            height: "6px",
            borderInlineEnd: "2px solid",
            borderBlockEnd: "2px solid",
            color: "inherit",
            insetBlockStart: "37%",
            marginInlineStart: "10px",
            transition: "transform 300ms",
            transform: "rotate(45deg)",
            transformOrigin: "center center"
        }
    },
    "&.open::after": {
        [media.below.md]: {
            transform: "rotate(-135deg) translateY(-37%) translateX(-25%)"
        }
    },
    [media.above.md]: {
        margin: `0 ${token("space.200")}`,
        padding: `${token("space.200")} 0`,
        boxSizing: "border-box",
        borderBlockEnd: "3px solid white"
    },
    [media.below.md]: {
        width: "100%",
        padding: `${token("space.200")} 10px`,
        margin: `0 ${token("space.200")}`,
        insetInlineEnd: "10px"
    }
});

const dropdownStyle = css({
    backgroundColor: "#fff",
    border: "1px solid #f4f5f7",
    borderRadius: "4px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.25)",
    position: "absolute",
    insetBlockStart: "100%",
    insetInlineStart: "50%",
    transform: "translate(-50%, -8px)",
    zIndex: 10
});

const listStyle = css({
    "listStyle": "none",
    "paddingInlineStart": 0,
    "paddingBlockStart": "5px",
    "paddingBlockEnd": "5px",
    "position": "relative",
    "zIndex": 1,
    "::before": {
        backgroundColor: "#fff",
        insetBlockStart: 0,
        zIndex: 0,
        insetBlockEnd: "100%",
        insetInlineStart: "50%",
        border: "9px solid rgba(0, 0, 0, 0)",
        content: '" "',
        height: 0,
        width: 0,
        position: "absolute",
        pointerEvents: "none",
        transform: "translateX(-50%)"
    },
    "::after": {
        filter: "drop-shadow(0 0 3px rgba(0, 0, 0, 0.25))",
        zIndex: -1,
        insetBlockEnd: "100%",
        insetInlineStart: "50%",
        border: "8px solid rgba(0, 0, 0, 0)",
        content: '" "',
        height: 0,
        width: 0,
        position: "absolute",
        pointerEvents: "none",
        borderBlockEndColor: "#fff",
        transform: "translateX(-50%)"
    }
});

const headerStyle = css({
    display: "block",
    padding: "4px 30px",
    whiteSpace: "nowrap",
    textDecoration: "none",
    fontSize: "1rem",
    color: "#253858",
    margin: "12px 0 0 0",
    textTransform: "uppercase",
    fontWeight: 700,
    lineHeight: 1.25,
    letterSpacing: "1px"
});

const listItemStyle = css({
    "position": "relative",
    "marginBlockStart": "12px",
    "marginBlockEnd": "12px",
    ".selected": {
        "fontWeight": 600,
        ":hover": {
            backgroundColor: "#253858",
            color: "#ffffff"
        }
    }
});

const linkStyle = css({
    "fontSize": "1rem",
    "lineHeight": 1.11111111,
    "display": "block",
    "padding": "4px 30px",
    "whiteSpace": "nowrap",
    "textDecoration": "none",
    "color": "#0052cc",
    ":hover": {
        borderBlockEndColor: "#000000",
        backgroundColor: "#0052cc",
        color: "#ffffff"
    }
});

const mobileDropdownStyle = css({
    ul: {
        padding: 0
    }
});

export const ProductNavDropdown = ({
    label,
    isMobile = false,
    groups,
    dataProductKey = "jiraSoftware"
}: ProductNavDropdownProps) => {
    const [isDropdownVisible, setDropdownVisible] = useState(false);
    const [isMobileDropdownVisible, setMobileDropdownVisible] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);
    useOutsideEvent(dropdownRef, "click", () => setDropdownVisible(false));
    useOutsideEvent(dropdownRef, "focusin", () => setDropdownVisible(false));

    const innerRef = useRef<HTMLDivElement>(null);
    useOutsideEvent(innerRef, "click", () => setDropdownVisible(false));
    useOutsideEvent(innerRef, "focusin", () => setDropdownVisible(false));

    const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
        event.preventDefault();
        setMobileDropdownVisible((state) => !state);
    };

    const handleClick = () => setMobileDropdownVisible((state) => !state);

    const handleMouseEnter = () => setDropdownVisible(true);

    const handleMouseLeave = () => setDropdownVisible(false);

    const handleKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter") {
            setDropdownVisible((state) => !state);
        }
    };

    useEffect(() => {
        if (isDropdownVisible || isMobileDropdownVisible) {
            Tracking.autoTrackLinks(true);
        }
    }, [isDropdownVisible, isMobileDropdownVisible]);

    const renderGroups = (dataProductKey: ProductKeyTypes) =>
        groups?.map(({ header, items, hideHeader }, groupIndex) => (
            <Fragment key={`${header}-${groupIndex}`}>
                {!hideHeader && (
                    <li>
                        <h5 css={headerStyle}>{header}</h5>
                    </li>
                )}
                {items.map(
                    (
                        {
                            label: itemLabel,
                            labelEnglish: itemLabelEnglish,
                            href,
                            isSelected
                        },
                        index
                    ) => (
                        <li key={`${itemLabel}-${index}`} css={listItemStyle}>
                            <a
                                className={isSelected ? "selected" : ""}
                                href={href}
                                css={linkStyle}
                                id={`dropdown-${label.toLowerCase()}-${index}`}
                                data-event="clicked"
                                data-event-component="linkButton"
                                data-event-container="navbarDropdownLink"
                                data-product-key={dataProductKey}
                                data-label={itemLabel}
                                data-label-english={itemLabelEnglish}
                            >
                                {itemLabel}
                            </a>
                        </li>
                    )
                )}
            </Fragment>
        ));

    return (
        <div>
            <div
                ref={dropdownRef}
                onTouchEnd={handleTouchEnd}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
                onFocus={handleMouseEnter}
                onKeyUp={handleKeyUp}
            >
                <a
                    id={`dropdown-${label.toLowerCase()}`}
                    href="#"
                    data-event="clicked"
                    data-label={label}
                    data-event-component="linkButton"
                    data-event-container="navbarDropdown"
                    data-product-key={dataProductKey}
                    css={parentItemStyle}
                    className={isMobileDropdownVisible ? "open" : ""}
                >
                    {label}
                </a>
                {!isMobile && isDropdownVisible ? (
                    <div css={dropdownStyle} ref={innerRef}>
                        <ul css={listStyle}>{renderGroups(dataProductKey)}</ul>
                    </div>
                ) : null}
            </div>
            <div>
                {isMobile && isMobileDropdownVisible ? (
                    <div css={mobileDropdownStyle} ref={innerRef}>
                        <ul>{renderGroups(dataProductKey)}</ul>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default ProductNavDropdown;
